// ==============================
// Imports
// ==============================

// MUI Components
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// Libraries
import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Local components
import { useTranslation } from '../i18n';
import data from '../utils/MiniDrawerData';
import UserMenu from './UserMenu';
import DarkModeToggle from './common/DarkModeToggle';
import LanguageSwitcher from './common/LanguageSwitcher';
import { useIsDownLg } from '../hooks/useResponsive';

// ==============================
// Types
// ==============================
interface TabItem {
  id: string | number;
  img?: React.ReactNode;
  label: string;
  labelKey?: string;
  path?: string;
  children?: TabItem[];
  roles?: string[];
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const drawerWidth = 280;

// ==============================
// Styles
// ==============================
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));
// ==============================
// Handler
// ==============================

// ==============================
// Components
// ==============================
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// ==============================
// Render
// ==============================
export default function MiniDrawer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  type TabUrl = {
    path: string;
  };

  const theme = useTheme();
  const { t } = useTranslation();

  // Resolve a nav item's display text: translated when it carries a labelKey,
  // otherwise its literal English label (so untranslated items still render).
  const navLabel = (item: TabItem) =>
    item.labelKey ? t(item.labelKey) : item.label;

  // Below the desktop breakpoint the sidebar becomes a temporary overlay so it
  // stops eating horizontal space on tablets/phones.
  const isDownLg = useIsDownLg();

  const navigate = useNavigate();

  // Default the drawer closed on tablet/phone and open on desktop, following the
  // breakpoint. (The page owns the `open` state; we just steer it.)
  // Only react to the breakpoint crossing, not to every render.
  React.useEffect(() => {
    setOpen(!isDownLg);
  }, [isDownLg]);

  const tabClickEvent = (url: TabUrl) => {
    navigate(url.path);
    // On the overlay drawer, navigating should dismiss it.
    if (isDownLg) setOpen(false);
  };

  // Setting Collapse — keep the group expanded when on one of its sub-pages
  // (the drawer remounts on navigation, so we derive the initial state from the
  // current route instead of always collapsing).
  const location = useLocation();
  const settingsGroup = data().find((item: TabItem) => item.children);
  const isOnSettingsChild =
    settingsGroup?.children?.some(
      (child) => child.path && location.pathname.startsWith(child.path),
    ) ?? false;
  const [openCollapse, setOpenCollapse] = React.useState(isOnSettingsChild);
  const handleCollapse = () => {
    setOpenCollapse(!openCollapse);
  };

  const user = JSON.parse(
    localStorage.getItem('aquatics_hub_2025_user') || 'null',
  );
  const canSee = (item: TabItem) =>
    !item.roles || item.roles.includes(user?.role);
  const dataRes = data()
    .filter(canSee)
    .map((item) => ({
      ...item,
      children: item.children?.filter(canSee),
    }));
  const handleLogout = () => {
    localStorage.removeItem('aquatics_hub_2025_tokens');
    localStorage.removeItem('aquatics_hub_2025_user');
    navigate('/signin');
  };

  return (
    <>
      <Box>
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open && !isDownLg}
          style={{ background: '#4da7db' }}
        >
          <Toolbar>
            {/* Menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setOpen(!open)}
              edge="start"
              sx={{
                marginRight: 5,
                // On desktop the mini-rail already shows; hide the hamburger when
                // expanded. On the overlay (tablet/phone) always keep it.
                ...(open && !isDownLg && { display: 'none' }),
              }}
            >
              <MenuIcon style={{ color: 'white' }} />
            </IconButton>
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              {/* Official logo + slogan */}
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1.5}
              >
                <Box
                  component="img"
                  src={
                    theme.palette.mode === 'dark'
                      ? '/Bluename_edited_white.avif'
                      : '/Bluename_edited.avif'
                  }
                  alt="Aquatics HUB"
                  sx={{
                    height: 40,
                    width: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
                <Typography
                  variant="caption"
                  noWrap
                  component="div"
                  className="text-white"
                  sx={{ opacity: 0.85, fontStyle: 'italic', lineHeight: 1.2 }}
                >
                  Creating a swimming culture in Hanoi
                </Typography>
              </Box>
              {/* Version */}
              {/* Dropdown select: stable version or head version — hidden. */}
              {/* <Select
                size="small"
                labelId="version-select-label"
                value={
                  localStorage.getItem('aquatics_hub_2025_version') ||
                  getEnvVariable('REACT_APP_HEAD_VERSION')
                }
                onChange={(e) => {
                  localStorage.setItem(
                    'aquatics_hub_2025_version',
                    e.target.value,
                  );
                }}
                label="Version"
                sx={{
                  my: 1,
                  color: 'white',
                  border: '1px solid white',
                }}
              >
                <MenuItem
                  value={getEnvVariable('REACT_APP_HEAD_VERSION')}
                  onClick={() => window.location.reload()}
                >
                  Version: {getEnvVariable('REACT_APP_HEAD_VERSION')}
                </MenuItem>
                <MenuItem
                  value={getEnvVariable('REACT_APP_STABLE_VERSION')}
                  onClick={() => {
                    // Move to stable url
                    window.location.href = getEnvVariable(
                      'REACT_APP_STABLE_VERSION_URL',
                    );
                  }}
                >
                  Version: {getEnvVariable('REACT_APP_STABLE_VERSION')}
                </MenuItem>
              </Select> */}

              {/* <Typography
                variant="caption"
                noWrap
                component="div"
                fontStyle={'italic'}
                className="text-white"
                sx={{ alignSelf: 'flex-end' }}
              >
                {`Version: ${getEnvVariable('REACT_APP_STABLE_VERSION')}`}
              </Typography> */}
            </Box>

            {/* Right side: language, theme, user */}
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LanguageSwitcher />
              <DarkModeToggle />
              <UserMenu username={user?.name || ''} onLogout={handleLogout} />
            </Box>
          </Toolbar>
          {/* Other item */}
        </AppBar>
        {(() => {
          const drawerContent = (
            <>
              <DrawerHeader>
                <IconButton onClick={() => setOpen(!open)}>
                  {theme.direction === 'rtl' ? (
                    <ChevronRightIcon />
                  ) : (
                    <ChevronLeftIcon />
                  )}
                </IconButton>
              </DrawerHeader>
              <Divider />
              <List>
                {dataRes.map((text: TabItem) => {
                  if (text.children)
                    return (
                      <React.Fragment key={text.id}>
                        <ListItem disablePadding sx={{ display: 'block' }}>
                          <ListItemButton
                            sx={{
                              minHeight: 48,
                              justifyContent: open ? 'initial' : 'center',
                              px: 2.5,
                            }}
                            onClick={handleCollapse}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: open ? 3 : 'auto',
                                justifyContent: 'center',
                              }}
                            >
                              {text.img}
                            </ListItemIcon>
                            <ListItemText
                              primary={navLabel(text)}
                              sx={{ opacity: open ? 1 : 0 }}
                            />
                            {openCollapse ? <ExpandLess /> : <ExpandMore />}
                          </ListItemButton>
                        </ListItem>
                        <Collapse
                          in={openCollapse}
                          timeout="auto"
                          unmountOnExit
                        >
                          <List component="div" disablePadding>
                            {(text.children || [])
                              .filter(canSee)
                              .map((child: TabItem) => (
                                <ListItem
                                  key={child.id}
                                  disablePadding
                                  sx={{ display: 'block' }}
                                >
                                  <ListItemButton
                                    sx={{
                                      minHeight: 48,
                                      justifyContent: open
                                        ? 'initial'
                                        : 'center',
                                      pr: 2.5,
                                      pl: 5,
                                    }}
                                    onClick={() =>
                                      tabClickEvent(child as TabUrl)
                                    }
                                  >
                                    <ListItemIcon
                                      sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {child.img}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={navLabel(child)}
                                      sx={{ opacity: open ? 1 : 0 }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  else
                    return (
                      <ListItem
                        key={text.id}
                        disablePadding
                        sx={{ display: 'block' }}
                      >
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                            px: 2.5,
                          }}
                          onClick={() => tabClickEvent(text as TabUrl)}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 3 : 'auto',
                              justifyContent: 'center',
                            }}
                          >
                            {text.img}
                          </ListItemIcon>
                          <ListItemText
                            primary={navLabel(text)}
                            sx={{ opacity: open ? 1 : 0 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                })}
              </List>
            </>
          );

          // Below lg: an overlay drawer that doesn't consume layout width.
          // lg+: the original permanent mini-rail.
          return isDownLg ? (
            <MuiDrawer
              variant="temporary"
              open={open}
              onClose={() => setOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  boxSizing: 'border-box',
                },
              }}
            >
              {drawerContent}
            </MuiDrawer>
          ) : (
            <Drawer variant="permanent" open={open}>
              {drawerContent}
            </Drawer>
          );
        })()}
      </Box>
    </>
  );
}
